// Extracted from original/src/components/StoreSalesAndReturns.tsx (ADR-08 §2.1 "copy, then cut logic"):
//   sales invoices tab: four KPI cards, search / warehouse / date preset / payment / product filter bar, custom range,
//   the invoices table with payment and return-state badges and the row actions                       :664-1138
//   returns tab: the return form card (invoice / manual mode switch, invoice picker, items table with sold / returned /
//   returnable / price / return quantity, manual product picker, refund method, box / customer account, reason
//   presets, notes, summary banner) and the returns history table                                      :1140-1741
// Kept: every class string, icon, the card colours, the badges ("آجل - دين", "خصم أمانة", "نقداً (واصل)", "مرتجع
// بالكامل", "مرتجع جزئي (n/m)"), the reason presets, the empty states with "إنشاء أول قائمة بيع".
// Cut: list building by item text (`is_sales_return || item.includes('مرتجع')`, R-28), `salesStats` arithmetic and the
// return limits computed from `related_movement_id` / `transaction_group_id` (R-20) → the server's operations
// classified by type (D-115) with the returned units per line, passed in as preformatted rows; `window.confirm` delete
// → `onCancel` (reason + own PIN, D-091 reversal); `alert` on a fully returned invoice → the disabled button.
// Changed by decision: the invoice return sends product × warehouse lines; what was sold, its price and its cost come
// from the stored sale (D-113, D-310); a manual return needs `store_manual_return` and a reason (D-113); a refund goes
// to the account chosen by id (D-084). "صافي أرباح المبيعات" subtracts the returns (D-115), the figure comes from the
// container. The refund total is shown by the server preview in the PIN step (ADR-08 §2.1 item 2).
// Added: data-testid attributes.
import { motion } from 'motion/react';
import { Ban, CheckCircle2, Clock, DollarSign, Eye, FileText, Package, Phone, Plus, Printer, Receipt, RotateCcw, Search, ShoppingBag, Sparkles, Trash2, TrendingUp, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CustomSelect, type SelectOption } from '../components/CustomSelect.js';
import { EnglishDatePicker } from '../components/EnglishDatePicker.js';
import { NumericInput } from '../components/NumericInput.js';
import { useI18n } from '../i18n-context.js';

export type StorePaymentKind = 'cash' | 'debt' | 'trust';
export type StoreDatePreset = 'all' | 'today' | 'week' | 'custom';

export interface StoreSaleRow {
  readonly id: string;
  readonly invoiceLabel: string;
  readonly dateLabel: string;
  readonly buyer: string;
  readonly buyerPhone: string | null;
  readonly items: ReadonlyArray<{ readonly name: string; readonly qty: string }>;
  readonly payment: StorePaymentKind;
  readonly returnState: 'none' | 'partial' | 'full';
  readonly returnedUnits: string;
  readonly soldUnits: string;
  readonly totalLabel: string;
  readonly profitLabel: string;
  readonly operator: string;
  readonly cancelled: boolean;
}

export interface StoreReturnRow {
  readonly id: string;
  readonly invoiceLabel: string;
  readonly dateLabel: string;
  readonly buyer: string;
  readonly details: string;
  readonly boxName: string | null;
  readonly reason: string;
  readonly refundLabel: string;
  readonly operator: string;
  readonly cancelled: boolean;
}

/** One line of the invoice chosen for a return (the server's figures, preformatted). */
export interface StoreReturnInvoiceLine {
  readonly key: string;
  readonly productId: string;
  readonly warehouseId: string;
  readonly name: string;
  readonly sold: string;
  readonly returned: string;
  /** whole units still returnable (sold − returned) */
  readonly returnable: number;
  readonly priceLabel: string;
  /** unit sale price as typed default refund (decimal string) */
  readonly unitPrice: string;
}

export interface StoreReturnValues {
  readonly mode: 'invoice' | 'manual';
  readonly saleId: string | null;
  readonly lines: ReadonlyArray<{ readonly productId: string; readonly warehouseId: string; readonly quantity: string; readonly unitRefund: string }>;
  readonly method: 'cash' | 'customer_debt' | 'customer_trust';
  readonly cashAccountId: string;
  readonly customerAccountId: string;
  readonly reason: string;
  readonly note: string;
}

export interface StoreSalesAndReturnsProps {
  readonly activeSubTab: 'sales_invoices' | 'sales_returns';
  readonly onSubTab: (t: 'sales_invoices' | 'sales_returns') => void;
  // sales
  readonly stats: { readonly revenueLabel: string; readonly invoices: number; readonly unitsLabel: string; readonly profitLabel: string };
  readonly search: string;
  readonly onSearch: (v: string) => void;
  readonly warehouseOptions: readonly SelectOption[];
  readonly warehouseFilter: string;
  readonly onWarehouseFilter: (v: string) => void;
  readonly datePreset: StoreDatePreset;
  readonly onDatePreset: (v: StoreDatePreset) => void;
  readonly from: string;
  readonly to: string;
  readonly onFrom: (v: string) => void;
  readonly onTo: (v: string) => void;
  readonly paymentFilter: 'all' | StorePaymentKind;
  readonly onPaymentFilter: (v: 'all' | StorePaymentKind) => void;
  readonly productOptions: readonly SelectOption[];
  readonly productFilter: string;
  readonly onProductFilter: (v: string) => void;
  readonly sales: readonly StoreSaleRow[];
  readonly canCancel: boolean;
  readonly onView: (id: string) => void;
  readonly onPrint: (id: string) => void;
  readonly onCancel: (id: string) => void;
  readonly onNewSale: () => void;
  // returns
  readonly saleOptions: readonly SelectOption[];
  readonly selectedSaleId: string;
  readonly onSelectSale: (id: string) => void;
  readonly invoiceLines: readonly StoreReturnInvoiceLine[];
  readonly invoiceFullyReturned: boolean;
  readonly canManualReturn: boolean;
  readonly manualProductOptions: readonly SelectOption[];
  readonly cashAccountOptions: readonly SelectOption[];
  readonly defaultCashAccountId: string;
  readonly debtAccountOptions: readonly SelectOption[];
  readonly trustAccountOptions: readonly SelectOption[];
  readonly busy: boolean;
  readonly onSubmitReturn: (v: StoreReturnValues) => void;
  readonly returns: readonly StoreReturnRow[];
  readonly onViewReturn: (id: string) => void;
  readonly onCancelReturn: (id: string) => void;
}

export function StoreSalesAndReturns(p: StoreSalesAndReturnsProps) {
  const { t } = useI18n();
  const [returnMode, setReturnMode] = useState<'invoice' | 'manual'>('invoice');
  const [qty, setQty] = useState<Record<string, string>>({});
  const [refundPrice, setRefundPrice] = useState<Record<string, string>>({});
  const [manualProduct, setManualProduct] = useState('');
  const [manualQty, setManualQty] = useState('1');
  const [manualRefund, setManualRefund] = useState('');
  const [method, setMethod] = useState<'cash' | 'customer_debt' | 'customer_trust'>('cash');
  const [cashAccountId, setCashAccountId] = useState(p.defaultCashAccountId);
  const [customerAccountId, setCustomerAccountId] = useState('');
  const [reason, setReason] = useState('طلب العميل / زائد عن الحاجة');
  const [note, setNote] = useState('');

  useEffect(() => {
    setQty({});
    setRefundPrice({});
  }, [p.selectedSaleId]);
  useEffect(() => {
    if (!cashAccountId && p.defaultCashAccountId) setCashAccountId(p.defaultCashAccountId);
  }, [p.defaultCashAccountId, cashAccountId]);

  const reasons: SelectOption[] = [
    { id: 'طلب العميل / زائد عن الحاجة', label: t('st.reason_request') },
    { id: 'تالف / معيب', label: t('st.reason_damaged') },
    { id: 'غير مطابق للمواصفات', label: t('st.reason_spec') },
    { id: 'استبدال بصنف آخر', label: t('st.reason_exchange') },
    { id: 'أخرى', label: t('st.reason_other') },
  ];

  const invoiceUnits = p.invoiceLines.reduce((acc, l) => acc + (Number.parseInt(qty[l.key] ?? '0', 10) || 0), 0);
  const manualUnits = Number.parseInt(manualQty || '0', 10) || 0;
  const totalUnits = returnMode === 'invoice' ? invoiceUnits : manualUnits;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const lines =
      returnMode === 'invoice'
        ? p.invoiceLines.filter((l) => (Number.parseInt(qty[l.key] ?? '0', 10) || 0) > 0).map((l) => ({ productId: l.productId, warehouseId: l.warehouseId, quantity: String(Number.parseInt(qty[l.key]!, 10)), unitRefund: refundPrice[l.key] ?? l.unitPrice }))
        : manualProduct
          ? [{ productId: manualProduct.split('|')[0]!, warehouseId: manualProduct.split('|')[1]!, quantity: manualQty, unitRefund: manualRefund || '0' }]
          : [];
    p.onSubmitReturn({ mode: returnMode, saleId: returnMode === 'invoice' ? p.selectedSaleId || null : null, lines, method, cashAccountId, customerAccountId, reason: reason === 'أخرى' && note.trim() ? note.trim() : reason, note });
  };

  const datePresetBtn = (k: StoreDatePreset, label: string) => (
    <button
      type="button"
      onClick={() => p.onDatePreset(k)}
      className={`flex-1 py-1.5 rounded-xl text-[11px] font-bold transition-all ${p.datePreset === k ? 'bg-white dark:bg-[#21262d] text-blue-600 dark:text-blue-400 shadow-sm' : 'text-gray-500 hover:text-gray-800 dark:hover:text-white'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="store-sales-returns">
      {p.activeSubTab === 'sales_invoices' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Sales Stats KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden group">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.sales_total')}</span>
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                  <DollarSign size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-gray-900 dark:text-white" data-testid="store-sales-revenue">
                {p.stats.revenueLabel} <span className="text-xs font-bold text-gray-400">د.ع</span>
              </div>
              <div className="mt-2 text-[11px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1">
                <CheckCircle2 size={13} />
                <span>{t('st.sales_total_sub')}</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden group">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.sales_count')}</span>
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                  <Receipt size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-gray-900 dark:text-white">
                {p.stats.invoices} <span className="text-xs font-bold text-gray-400">{t('st.sales_count_unit')}</span>
              </div>
              <div className="mt-2 text-[11px] text-blue-600 dark:text-blue-400 font-bold flex items-center gap-1">
                <FileText size={13} />
                <span>{t('st.sales_count_sub')}</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden group">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.sales_units')}</span>
                <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                  <ShoppingBag size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-gray-900 dark:text-white">
                {p.stats.unitsLabel} <span className="text-xs font-bold text-gray-400">{t('st.sales_units_unit')}</span>
              </div>
              <div className="mt-2 text-[11px] text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1">
                <Package size={13} />
                <span>{t('st.sales_units_sub')}</span>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm relative overflow-hidden group">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('st.sales_profit')}</span>
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                  <TrendingUp size={20} />
                </div>
              </div>
              <div className="text-2xl font-black font-mono text-purple-600 dark:text-purple-400" data-testid="store-sales-profit">
                {p.stats.profitLabel} <span className="text-xs font-bold text-gray-400">د.ع</span>
              </div>
              <div className="mt-2 text-[11px] text-purple-600 dark:text-purple-400 font-bold flex items-center gap-1">
                <Sparkles size={13} />
                <span>{t('st.sales_profit_sub')}</span>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="bg-white dark:bg-[#0d1117] p-5 rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={p.search}
                  onChange={(e) => p.onSearch(e.target.value)}
                  placeholder={t('st.sales_search')}
                  className="w-full pl-3 pr-9 py-2.5 rounded-2xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>
              {p.warehouseOptions.length > 1 && (
                <div>
                  <CustomSelect
                    value={p.warehouseFilter}
                    onChange={(val) => p.onWarehouseFilter(String(val))}
                    options={[...p.warehouseOptions]}
                    placeholder={t('st.filter_wh_all')}
                    headerTitle={t('st.filter_wh_header')}
                    searchPlaceholder={t('st.filter_wh_search')}
                    size="md"
                  />
                </div>
              )}
              <div className="flex items-center gap-1 bg-gray-50 dark:bg-[#161b22] p-1 rounded-2xl border border-gray-200 dark:border-[#30363d]">
                {datePresetBtn('all', t('st.date_all'))}
                {datePresetBtn('today', t('st.date_today'))}
                {datePresetBtn('week', t('st.date_week'))}
                {datePresetBtn('custom', t('st.date_custom'))}
              </div>
              <div>
                <CustomSelect
                  value={p.paymentFilter}
                  onChange={(val) => p.onPaymentFilter(val as 'all' | StorePaymentKind)}
                  options={[
                    { id: 'all', label: t('st.pay_all') },
                    { id: 'cash', label: t('st.pay_cash'), badge: t('st.pay_cash_badge') },
                    { id: 'debt', label: t('st.pay_debt'), badge: t('st.pay_debt_badge') },
                    { id: 'trust', label: t('st.pay_trust'), badge: t('st.pay_trust_badge') },
                  ]}
                  placeholder={t('st.pay_all')}
                  headerTitle={t('st.pay_header')}
                  searchPlaceholder={t('st.pay_search')}
                  size="md"
                />
              </div>
              <div>
                <CustomSelect
                  value={p.productFilter}
                  onChange={(val) => p.onProductFilter(String(val))}
                  options={[{ id: 'all', label: t('st.product_filter_all') }, ...p.productOptions]}
                  placeholder={t('st.product_filter_all')}
                  headerTitle={t('st.product_filter_header')}
                  searchPlaceholder={t('st.product_filter_search')}
                  size="md"
                />
              </div>
            </div>
            {p.datePreset === 'custom' && (
              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-100 dark:border-[#30363d]">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500">{t('st.from_date')}</span>
                  <EnglishDatePicker value={p.from} onChange={(val) => p.onFrom(val)} className="!py-1.5 !text-xs !bg-gray-50 dark:!bg-[#161b22]" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500">{t('st.to_date')}</span>
                  <EnglishDatePicker value={p.to} onChange={(val) => p.onTo(val)} className="!py-1.5 !text-xs !bg-gray-50 dark:!bg-[#161b22]" />
                </div>
                {(p.from || p.to) && (
                  <button
                    type="button"
                    onClick={() => {
                      p.onFrom('');
                      p.onTo('');
                    }}
                    className="text-xs text-rose-500 hover:underline font-bold"
                  >
                    {t('st.clear')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Sales Invoices Table */}
          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-[#30363d] flex items-center justify-between bg-gray-50/50 dark:bg-[#161b22]/50">
              <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                <Receipt size={18} className="text-blue-600" />
                <span>{t('st.sales_list_title', { n: p.sales.length })}</span>
              </h3>
              <span className="text-xs font-mono font-bold text-gray-500">
                {t('st.displayed_sum')}
                <strong className="text-emerald-600 dark:text-emerald-400">{p.stats.revenueLabel} د.ع</strong>
              </span>
            </div>
            {p.sales.length === 0 ? (
              <div className="p-12 text-center text-gray-400 space-y-3">
                <Receipt size={48} className="mx-auto text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-bold">{t('st.sales_empty')}</p>
                <button type="button" onClick={p.onNewSale} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-xs font-black rounded-xl hover:bg-blue-700 transition-colors shadow-md">
                  <Plus size={16} />
                  <span>{t('st.sales_first')}</span>
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#161b22] text-gray-600 dark:text-gray-400 font-black">
                      <th className="py-3 px-4 text-center w-16">{t('st.col_entry')}</th>
                      <th className="py-3 px-4">{t('st.col_datetime')}</th>
                      <th className="py-3 px-4">{t('st.col_buyer')}</th>
                      <th className="py-3 px-4">{t('st.col_sold_items')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_payment')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_total')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_profit')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_operator')}</th>
                      <th className="py-3 px-4 text-center w-36">{t('st.col_actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {p.sales.map((sale) => (
                      <tr key={sale.id} data-testid={`store-sale-${sale.id}`} className={`hover:bg-blue-50/30 dark:hover:bg-blue-950/10 transition-colors ${sale.cancelled ? 'opacity-50' : ''}`}>
                        <td className="py-3.5 px-4 text-center font-mono font-black text-gray-500">
                          <span className="bg-gray-100 dark:bg-[#21262d] px-2 py-1 rounded-lg text-[11px] text-gray-800 dark:text-gray-200">#{sale.invoiceLabel}</span>
                        </td>
                        <td className="py-3.5 px-4 font-medium text-gray-700 dark:text-gray-300">
                          <div className="flex items-center gap-1.5">
                            <Clock size={13} className="text-gray-400 shrink-0" />
                            <span className="font-mono text-[11px]">{sale.dateLabel}</span>
                          </div>
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="font-black text-gray-900 dark:text-white flex items-center gap-1.5">
                            <User size={13} className="text-blue-500 shrink-0" />
                            <span>{sale.buyer}</span>
                          </div>
                          {sale.buyerPhone && (
                            <div className="text-[10px] text-gray-400 font-mono flex items-center gap-1 mt-0.5">
                              <Phone size={10} />
                              <span>{sale.buyerPhone}</span>
                            </div>
                          )}
                        </td>
                        <td className="py-3.5 px-4">
                          <div className="space-y-1">
                            {sale.items.slice(0, 2).map((it, idx) => (
                              <div key={`sale-item-${sale.id}-${idx}`} className="flex items-center gap-2 text-xs">
                                <span className="font-bold text-gray-800 dark:text-gray-200 truncate max-w-[160px]">{it.name}</span>
                                <span className="text-[10px] font-mono font-bold bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">×{it.qty}</span>
                              </div>
                            ))}
                            {sale.items.length > 2 && <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">+{sale.items.length - 2} {t('st.more_items')}</span>}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {sale.payment === 'debt' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-900/50">{t('st.badge_debt')}</span>
                            ) : sale.payment === 'trust' ? (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-purple-50 dark:bg-purple-950/40 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-900/50">{t('st.badge_trust')}</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900/50">{t('st.badge_cash')}</span>
                            )}
                            {sale.cancelled ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-700">{t('st.badge_cancelled')}</span>
                            ) : sale.returnState === 'full' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
                                <Ban size={10} />
                                <span>{t('st.badge_full_return')}</span>
                              </span>
                            ) : sale.returnState === 'partial' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[9px] font-black bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-800">
                                <RotateCcw size={10} />
                                <span>{t('st.badge_partial_return', { r: sale.returnedUnits, s: sale.soldUnits })}</span>
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-3.5 px-4 text-center font-mono font-black text-sm text-emerald-600 dark:text-emerald-400">
                          {sale.totalLabel} <span className="text-[10px] font-bold">د.ع</span>
                        </td>
                        <td className="py-3.5 px-4 text-center font-mono font-bold text-purple-600 dark:text-purple-400">
                          {sale.profitLabel} <span className="text-[10px] font-bold">د.ع</span>
                        </td>
                        <td className="py-3.5 px-4 text-center text-gray-500 font-medium text-[11px]">{sale.operator || '-'}</td>
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button type="button" onClick={() => p.onView(sale.id)} title={t('st.view_full')} className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-blue-200/60 dark:border-blue-900/40">
                              <Eye size={15} />
                            </button>
                            <button type="button" data-testid={`store-sale-print-${sale.id}`} onClick={() => p.onPrint(sale.id)} title={t('st.print_invoice')} className="p-1.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors border border-gray-200 dark:border-gray-700">
                              <Printer size={15} />
                            </button>
                            <button
                              type="button"
                              data-testid={`store-sale-return-${sale.id}`}
                              disabled={sale.returnState === 'full' || sale.cancelled}
                              onClick={() => {
                                p.onSelectSale(sale.id);
                                setReturnMode('invoice');
                                p.onSubTab('sales_returns');
                              }}
                              title={sale.returnState === 'full' ? t('st.already_returned') : t('st.return_from_invoice')}
                              className={`p-1.5 rounded-lg transition-colors border ${sale.returnState === 'full' || sale.cancelled ? 'text-gray-400 bg-gray-100 dark:bg-[#161b22] border-gray-200 dark:border-gray-800 cursor-not-allowed opacity-60' : 'text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 border-rose-200/60 dark:border-rose-900/40'}`}
                            >
                              <RotateCcw size={15} />
                            </button>
                            {p.canCancel && !sale.cancelled && (
                              <button type="button" data-testid={`store-sale-cancel-${sale.id}`} onClick={() => p.onCancel(sale.id)} title={t('st.cancel_invoice')} className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors border border-rose-200/60 dark:border-rose-900/40">
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {p.activeSubTab === 'sales_returns' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Main Return Form Card */}
          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 dark:border-[#30363d] pb-4">
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
                  <RotateCcw className="text-rose-600" size={22} />
                  <span>{t('st.return_title')}</span>
                </h3>
                <p className="text-xs text-gray-500 font-medium mt-0.5">{t('st.return_sub')}</p>
              </div>
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-[#161b22] p-1 rounded-2xl border border-gray-200 dark:border-[#30363d]">
                <button
                  type="button"
                  onClick={() => setReturnMode('invoice')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 ${returnMode === 'invoice' ? 'bg-white dark:bg-[#21262d] text-rose-600 dark:text-rose-400 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                >
                  <Receipt size={14} />
                  <span>{t('st.return_mode_invoice')}</span>
                </button>
                {p.canManualReturn && (
                  <button
                    type="button"
                    data-testid="store-return-manual"
                    onClick={() => setReturnMode('manual')}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-1.5 ${returnMode === 'manual' ? 'bg-white dark:bg-[#21262d] text-rose-600 dark:text-rose-400 shadow-sm' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                  >
                    <Package size={14} />
                    <span>{t('st.return_mode_manual')}</span>
                  </button>
                )}
              </div>
            </div>

            <form onSubmit={submit} className="space-y-6">
              {returnMode === 'invoice' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                      <span>{t('st.return_pick_invoice')}</span>
                      <span className="text-[11px] text-gray-400 font-normal">{t('st.return_dup_hint')}</span>
                    </label>
                    <CustomSelect
                      value={p.selectedSaleId}
                      onChange={(val) => p.onSelectSale(String(val))}
                      options={[...p.saleOptions]}
                      placeholder={t('st.return_pick_placeholder')}
                      headerTitle={t('st.return_pick_header')}
                      searchPlaceholder={t('st.return_pick_search')}
                      size="md"
                    />
                  </div>
                  {p.invoiceFullyReturned && (
                    <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 text-xs font-bold text-rose-700 dark:text-rose-300">{t('st.return_fully_returned')}</div>
                  )}
                  {p.invoiceLines.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-xs font-black text-gray-700 dark:text-gray-300">{t('st.return_items_title')}</div>
                      <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-[#30363d]">
                        <table className="w-full text-right text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-50 dark:bg-[#161b22] text-gray-600 dark:text-gray-400 font-black">
                              <th className="py-2.5 px-3">{t('st.ret_col_product')}</th>
                              <th className="py-2.5 px-3 text-center">{t('st.ret_col_sold')}</th>
                              <th className="py-2.5 px-3 text-center">{t('st.ret_col_returned')}</th>
                              <th className="py-2.5 px-3 text-center">{t('st.ret_col_returnable')}</th>
                              <th className="py-2.5 px-3 text-center">{t('st.ret_col_price')}</th>
                              <th className="py-2.5 px-3 text-center">{t('st.ret_col_qty')}</th>
                              <th className="py-2.5 px-3 text-center">{t('st.ret_col_refund')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {p.invoiceLines.map((l) => (
                              <tr key={l.key} className={l.returnable === 0 ? 'opacity-50' : ''}>
                                <td className="py-2.5 px-3 font-bold text-gray-900 dark:text-white">{l.name}</td>
                                <td className="py-2.5 px-3 text-center font-mono">{l.sold}</td>
                                <td className="py-2.5 px-3 text-center font-mono text-rose-600">{l.returned}</td>
                                <td className="py-2.5 px-3 text-center font-mono text-emerald-600">{l.returnable === 0 ? t('st.ret_zero') : l.returnable}</td>
                                <td className="py-2.5 px-3 text-center font-mono">{l.priceLabel}</td>
                                <td className="py-2.5 px-3 text-center">
                                  <NumericInput
                                    value={qty[l.key] ?? ''}
                                    disabled={l.returnable === 0}
                                    onChange={(v) => setQty((q) => ({ ...q, [l.key]: v.replace(/\..*$/, '') }))}
                                    placeholder="0"
                                    className="w-20 px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-mono font-bold text-center"
                                  />
                                </td>
                                <td className="py-2.5 px-3 text-center">
                                  <NumericInput
                                    value={refundPrice[l.key] ?? l.unitPrice}
                                    disabled={l.returnable === 0}
                                    onChange={(v) => setRefundPrice((q) => ({ ...q, [l.key]: v }))}
                                    className="w-28 px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-mono font-bold text-center"
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {returnMode === 'manual' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.ret_manual_product')}</label>
                    <CustomSelect
                      value={manualProduct}
                      onChange={(val) => setManualProduct(String(val))}
                      options={[...p.manualProductOptions]}
                      placeholder={t('st.ret_manual_placeholder')}
                      headerTitle={t('st.ret_manual_header')}
                      searchPlaceholder={t('st.ret_manual_search')}
                      size="md"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.ret_manual_qty')}</label>
                    <NumericInput value={manualQty} onChange={(v) => setManualQty(v.replace(/\..*$/, ''))} className="w-full px-3 py-2.5 rounded-2xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-mono font-bold" />
                  </div>
                  <div>
                    <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.ret_manual_refund')}</label>
                    <NumericInput value={manualRefund} onChange={setManualRefund} className="w-full px-3 py-2.5 rounded-2xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-mono font-bold" />
                  </div>
                </div>
              )}

              {/* Refund Method & Account Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.refund_method')}</label>
                  <CustomSelect
                    value={method}
                    onChange={(val) => {
                      setMethod(val as typeof method);
                      setCustomerAccountId('');
                    }}
                    options={[
                      { id: 'cash', label: t('st.refund_cash'), badge: t('st.refund_cash_badge') },
                      { id: 'customer_debt', label: t('st.refund_debt'), badge: t('st.refund_debt_badge') },
                      { id: 'customer_trust', label: t('st.refund_trust'), badge: t('st.refund_trust_badge') },
                    ]}
                    headerTitle={t('st.refund_header')}
                    searchPlaceholder={t('st.refund_search')}
                    size="md"
                  />
                </div>
                {method === 'cash' ? (
                  <div>
                    <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.refund_box')}</label>
                    <CustomSelect value={cashAccountId} onChange={(val) => setCashAccountId(String(val))} options={[...p.cashAccountOptions]} placeholder={t('st.refund_box_placeholder')} headerTitle={t('st.refund_box_header')} searchPlaceholder={t('st.refund_box_search')} size="md" />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.refund_customer')}</label>
                    <CustomSelect
                      value={customerAccountId}
                      onChange={(val) => setCustomerAccountId(String(val))}
                      options={[...(method === 'customer_trust' ? p.trustAccountOptions : p.debtAccountOptions)]}
                      placeholder={t('st.refund_customer_placeholder')}
                      headerTitle={t('st.refund_customer_header')}
                      searchPlaceholder={t('st.refund_customer_search')}
                      size="md"
                    />
                  </div>
                )}
              </div>

              {/* Reason & Notes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.return_reason')}</label>
                  <CustomSelect value={reason} onChange={(val) => setReason(String(val))} options={reasons} headerTitle={t('st.reasons_header')} searchPlaceholder={t('st.reasons_search')} size="md" />
                </div>
                <div>
                  <label className="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{t('st.return_notes')}</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={t('st.return_notes_placeholder')}
                    className="w-full px-3 py-2.5 rounded-2xl bg-gray-50 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] text-xs font-bold focus:outline-none focus:border-rose-500"
                  />
                </div>
              </div>

              {/* Total Refund Banner & Execution Button */}
              <div className="bg-rose-50 dark:bg-rose-950/30 p-5 rounded-2xl border border-rose-200 dark:border-rose-900/50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <div className="text-xs font-bold text-rose-700 dark:text-rose-300">{t('st.return_summary')}</div>
                  <div className="flex items-center gap-4 text-sm font-black font-mono">
                    <span className="text-gray-900 dark:text-white">
                      {t('st.return_units')}
                      <strong className="text-blue-600 dark:text-blue-400">+{totalUnits}</strong>
                    </span>
                  </div>
                  <div className="text-[11px] text-rose-600/80 dark:text-rose-300/80 font-bold">{t('st.return_refund_hint')}</div>
                </div>
                <button
                  type="submit"
                  data-testid="store-return-submit"
                  disabled={p.busy || totalUnits === 0 || (returnMode === 'invoice' && !p.selectedSaleId)}
                  className="px-6 py-3 rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs flex items-center gap-2 shadow-lg shadow-rose-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RotateCcw size={16} />
                  <span>{p.busy ? t('st.processing') : t('st.return_execute')}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Returns History */}
          <div className="bg-white dark:bg-[#0d1117] rounded-3xl border border-gray-100 dark:border-[#30363d] shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-[#30363d] flex items-center justify-between bg-gray-50/50 dark:bg-[#161b22]/50">
              <h3 className="text-sm font-black text-gray-900 dark:text-white flex items-center gap-2">
                <RotateCcw size={18} className="text-rose-600" />
                <span>{t('st.returns_list_title', { n: p.returns.length })}</span>
              </h3>
            </div>
            {p.returns.length === 0 ? (
              <div className="p-12 text-center text-gray-400 space-y-3">
                <RotateCcw size={48} className="mx-auto text-gray-300 dark:text-gray-600" />
                <p className="text-sm font-bold">{t('st.returns_empty')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-right text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-[#30363d] bg-gray-50/80 dark:bg-[#161b22] text-gray-600 dark:text-gray-400 font-black">
                      <th className="py-3 px-4 text-center w-16">{t('st.col_entry')}</th>
                      <th className="py-3 px-4">{t('st.col_date')}</th>
                      <th className="py-3 px-4">{t('st.col_buyer')}</th>
                      <th className="py-3 px-4">{t('st.col_returned_items')}</th>
                      <th className="py-3 px-4">{t('st.col_reason')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_refund')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_operator')}</th>
                      <th className="py-3 px-4 text-center">{t('st.col_actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                    {p.returns.map((ret) => (
                      <tr key={ret.id} data-testid={`store-return-${ret.id}`} className={`hover:bg-rose-50/30 dark:hover:bg-rose-950/10 transition-colors ${ret.cancelled ? 'opacity-50' : ''}`}>
                        <td className="py-3.5 px-4 text-center font-mono font-black text-gray-500">
                          <span className="bg-gray-100 dark:bg-[#21262d] px-2 py-1 rounded-lg text-[11px] text-gray-800 dark:text-gray-200">#{ret.invoiceLabel}</span>
                        </td>
                        <td className="py-3.5 px-4 font-mono text-[11px] text-gray-700 dark:text-gray-300">{ret.dateLabel}</td>
                        <td className="py-3.5 px-4 font-black text-gray-900 dark:text-white">{ret.buyer}</td>
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-gray-800 dark:text-gray-200">{ret.details}</div>
                          {ret.boxName && <div className="text-[10px] text-gray-400 mt-0.5">{t('st.from_box')}{ret.boxName}</div>}
                        </td>
                        <td className="py-3.5 px-4 text-gray-600 dark:text-gray-300">{ret.reason}</td>
                        <td className="py-3.5 px-4 text-center font-mono font-black text-rose-600 dark:text-rose-400">
                          {ret.refundLabel} <span className="text-[10px] font-bold">د.ع</span>
                        </td>
                        <td className="py-3.5 px-4 text-center text-gray-500 font-medium text-[11px]">{ret.operator}</td>
                        <td className="py-3.5 px-4 text-center">
                          <div className="flex items-center justify-center gap-1.5">
                            <button type="button" onClick={() => p.onViewReturn(ret.id)} title={t('st.view_full')} className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-blue-200/60 dark:border-blue-900/40">
                              <Eye size={15} />
                            </button>
                            {p.canCancel && !ret.cancelled && (
                              <button type="button" data-testid={`store-return-cancel-${ret.id}`} onClick={() => p.onCancelReturn(ret.id)} title={t('st.cancel_return')} className="p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg transition-colors border border-rose-200/60 dark:border-rose-900/40">
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
}
